import { NOTIFICATION_PREVIEW_MAX_AGE_MS } from "../const/numbers"

// Mirrors the Android app's NotificationActions.kt so both clients treat a
// notification's CTA the same way.

/**
 * A notification's metadata as an object. The API documents it as an object
 * but the Android client receives it JSON-encoded, so accept either; anything
 * unparseable is treated as empty.
 */
export function parseNotificationMetadata(notification) {
    const isPlainObject = (value) =>
        !!value && typeof value === "object" && !Array.isArray(value)

    const raw = notification?.metadata
    if (isPlainObject(raw)) return raw
    if (typeof raw !== "string" || !raw.trim()) return {}

    try {
        const parsed = JSON.parse(raw)
        return isPlainObject(parsed) ? parsed : {}
    } catch {
        return {}
    }
}

/** CTA caption: metadata's `action_title` if present, else "View". */
export function notificationActionLabel(notification) {
    const title = parseNotificationMetadata(notification).action_title
    return typeof title === "string" && title.trim() ? title.trim() : "View"
}

/**
 * Where a notification's CTA should take the user: a route path, or null when
 * there is no page for it and the caller should open the notification center.
 * An invitation without a usable token falls back to the community page,
 * never a broken /join link.
 */
export function notificationActionPath(notification) {
    switch (notification?.action_type) {
        case "view-community":
            return "/community"
        case "accept-invitation": {
            const token = parseNotificationMetadata(notification).invitation_token
            return typeof token === "string" && token
                ? `/join/${encodeURIComponent(token)}`
                : "/community"
        }
        case "accept-checker-companion":
            return "/community/convoy"
        default:
            return null
    }
}

export const isUnreadInApp = (notification) =>
    notification?.channel === "in_app" &&
    (notification.status === "pending" || notification.status === "unread")

/**
 * Unread in-app notifications from the last week, newest first. The dashboard
 * card is a recent-activity preview, so an old unread item is left off rather
 * than sitting at the top of the page looking urgent.
 */
export function recentUnreadNotifications(
    notifications,
    now = Date.now(),
    maxAgeMs = NOTIFICATION_PREVIEW_MAX_AGE_MS,
) {
    const cutoff = now - maxAgeMs

    return (notifications || [])
        .filter((notification) => {
            if (!isUnreadInApp(notification)) return false
            const created = new Date(notification.created_at).getTime()
            // A missing or unparseable date is kept, as on Android.
            return Number.isNaN(created) || created >= cutoff
        })
        .sort(
            (a, b) =>
                new Date(b.created_at || 0).getTime() -
                new Date(a.created_at || 0).getTime(),
        )
}
